import React, { FC, ReactNode, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  GridLegacy,
  DialogProps,
  Alert,
  Stack,
  Box,
  SxProps,
} from '@mui/material';
import SegmentIconButton from 'ui-component/Segment/SegmentIconButton';
import { IconX } from '@tabler/icons-react';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import { PermissionScope } from 'types/apps';
import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import { useDeviceContext } from 'hooks/useDeviceContext';
import ConfirmDialog from 'ui-component/shared/ConfirmDialog';
import { ColorOptions } from 'types';

export type DialogTrackingNames = {
  close?: string;
  cancel?: string;
  save?: string;
};

interface CommonDialogProps extends Omit<DialogProps, 'title'> {
  title: ReactNode;
  onCloseDialog: () => void;
  trackingNames?: DialogTrackingNames;
  permissionScope?: PermissionScope;
  isSubmitting?: boolean;
  formSubmitError?: string | null;
  cancelButtonCopy?: string;
  submitButtonCopy?: string;
  submitDisabled?: boolean;
  submitButtonColor?: ColorOptions;
  additionalContent?: React.ReactNode;
  overrideComponents?: {
    actions?: ReactNode;
    actionAlert?: ReactNode;
  };
  noActions?: boolean;
  confirmClose?: boolean;
  confirmCloseBodyText?: string;
  confirmSubmit?: boolean;
  confirmSubmitBody?: ReactNode;
  hideCloseButton?: boolean;
  titleSx?: SxProps;
  inlineEditDialog?: boolean;
}

export interface FormDialogProps extends CommonDialogProps {
  isForm: true;
  onSubmit: (e: React.FormEvent) => void;
  onClickPrimaryButton?: never;
  additionalContentAboveForm?: React.ReactNode;
}

export interface NonFormDialogProps extends CommonDialogProps {
  isForm?: false;
  onSubmit?: never;
  onClickPrimaryButton?: () => void;
  additionalContentAboveForm?: never;
}

export type ExtendedDialogProps = FormDialogProps | NonFormDialogProps;

export const ExtendedDialog: FC<ExtendedDialogProps> = ({
  children,
  onCloseDialog,
  title,
  trackingNames,
  permissionScope,
  isSubmitting = false,
  isForm = false,
  onSubmit,
  formSubmitError,
  cancelButtonCopy,
  onClickPrimaryButton,
  submitButtonCopy,
  submitDisabled = false,
  submitButtonColor = 'primary',
  additionalContent,
  additionalContentAboveForm,
  overrideComponents = {},
  fullWidth = true,
  maxWidth = 'sm',
  noActions = false,
  confirmClose,
  confirmCloseBodyText,
  confirmSubmit,
  confirmSubmitBody,
  hideCloseButton = false,
  titleSx,
  inlineEditDialog = false,
  ...rest
}) => {
  const { desktopScreen, mobileVersion } = useDeviceContext();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmSubmitDialogOpen, setConfirmSubmitDialogOpen] = useState(false);

  const dialogContent = <DialogContent>{children}</DialogContent>;

  const additionalDialogContent = additionalContent ? (
    <Box sx={{ px: 3 }}>{additionalContent}</Box>
  ) : (
    <></>
  );

  const handleClose = () => {
    if (confirmClose) {
      setConfirmDialogOpen(true);
    } else {
      onCloseDialog();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    if (confirmSubmit) {
      setConfirmSubmitDialogOpen(true);
    } else {
      onSubmitPreventDefault(e);
    }
  };

  const onSubmitPreventDefault = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSubmit && onSubmit(e);
  };

  const defaultActions = (
    <Stack direction={desktopScreen ? 'row' : 'column-reverse'}>
      <SegmentButton
        onClick={handleClose}
        size={desktopScreen && !mobileVersion ? 'medium' : 'large'}
        color="primary"
        sx={{ mr: 1 }}
        trackingName={trackingNames?.cancel}
        fullWidth={!desktopScreen}
      >
        {cancelButtonCopy || 'Cancel'}
      </SegmentButton>
      <SegmentLoadingButton
        loading={isSubmitting}
        type={isForm ? 'submit' : 'button'}
        {...(onClickPrimaryButton
          ? {
              onClick: () =>
                confirmSubmit
                  ? setConfirmSubmitDialogOpen(true)
                  : onClickPrimaryButton(),
            }
          : {})}
        size={desktopScreen && !mobileVersion ? 'medium' : 'large'}
        color={submitButtonColor}
        variant="contained"
        permissionScope={permissionScope}
        trackingName={trackingNames?.save}
        disabled={submitDisabled}
        fullWidth={!desktopScreen}
      >
        {submitButtonCopy || 'Submit'}
      </SegmentLoadingButton>
    </Stack>
  );

  const actions = overrideComponents?.actions || defaultActions;

  const dialogActions = noActions ? (
    <></>
  ) : (
    <DialogActions sx={{ px: 3 }}>
      <Stack spacing={2} width="100%">
        {overrideComponents?.actionAlert && overrideComponents?.actionAlert}
        {formSubmitError && (
          <Alert severity="error" sx={{ my: 2 }}>
            {formSubmitError}
          </Alert>
        )}
        {desktopScreen && (
          <Stack
            direction="row"
            alignItems="flex-end"
            justifyContent="flex-end"
            width="100%"
          >
            {actions}
          </Stack>
        )}
        {!desktopScreen && actions}
      </Stack>
    </DialogActions>
  );

  if (!rest.open) return null;

  return (
    <>
      {confirmClose && confirmDialogOpen ? (
        <ConfirmDialog
          title="Confirm Close"
          dialogText={
            confirmCloseBodyText ??
            `Are you sure you want to close the ${title} dialog? You will lose all progress.`
          }
          dialogOpen={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={() => {
            onCloseDialog();
            setConfirmDialogOpen(false);
          }}
          confirmButtonText="Close Dialog"
          data-inline-edit-dialog={inlineEditDialog}
        />
      ) : null}
      {confirmSubmit && confirmSubmitDialogOpen ? (
        <ConfirmDialog
          title={`Confirm ${submitButtonCopy || 'Submit'}`}
          dialogText={
            confirmSubmitBody ||
            `Are you sure you want to ${
              submitButtonCopy?.toLowerCase() || 'submit'
            } the ${title}?`
          }
          dialogOpen={confirmSubmitDialogOpen}
          onClose={() => setConfirmSubmitDialogOpen(false)}
          onConfirm={() => {
            if (isForm) {
              const syntheticEvent = {
                preventDefault: () => {},
                stopPropagation: () => {},
              } as React.FormEvent<HTMLFormElement>;
              onSubmitPreventDefault(syntheticEvent);
            } else if (onClickPrimaryButton) {
              onClickPrimaryButton();
            }
            setConfirmSubmitDialogOpen(false);
          }}
          confirmButtonText={submitButtonCopy || 'Submit'}
          data-inline-edit-dialog={inlineEditDialog}
        />
      ) : null}
      <Dialog
        fullWidth={fullWidth}
        maxWidth={maxWidth}
        onClose={handleClose}
        fullScreen={!desktopScreen}
        {...rest}
        data-inline-edit-dialog={inlineEditDialog}
      >
        <DialogTitle
          sx={{
            p: typeof title === 'string' ? undefined : 0,
            '& > .MuiGrid-root': {
              width: '100%',
            },
          }}
        >
          <GridLegacy container>
            <GridLegacy item xs>
              {title}
            </GridLegacy>
            {!hideCloseButton && (
              <GridLegacy item xs="auto">
                <SegmentIconButton
                  onClick={handleClose}
                  trackingName={trackingNames?.close}
                  aria-label="Close Dialog"
                >
                  <IconX />
                </SegmentIconButton>
              </GridLegacy>
            )}
          </GridLegacy>
        </DialogTitle>
        {isForm ? (
          <>
            {additionalContentAboveForm && (
              <Box sx={{ px: 3, py: 2 }}>{additionalContentAboveForm}</Box>
            )}
            <form
              onSubmit={handleSubmit}
              style={{
                // this style is require to fix issues with scrolling per https://github.com/mui/material-ui/issues/13253
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {dialogContent}
              {dialogActions}
              {additionalDialogContent}
            </form>
          </>
        ) : (
          <>
            {dialogContent}
            {dialogActions}
            {additionalDialogContent}
          </>
        )}
      </Dialog>
    </>
  );
};
